import { Box, Inline, Stack, Tag, Text } from 'braid-design-system';
import React from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';

import {
  dateHolidayWarning,
  formatHolidayWarning,
} from '#shared/vicDates';
import type { VicDatesSnapshot } from '#shared/vicDatesTypes';
import { formatEventDisplayDate } from '#shared/eventUtils';

import * as styles from './EventDatePicker.css';
import {
  formDatesFromSelection,
  isoDatesToLocalDates,
  normalizeFormDates,
  removeIsoDate,
} from './eventDatePickerUtils';

type EventDatePickerProps = {
  id: string;
  dates: string[];
  onChange: (dates: string[]) => void;
  error?: string;
  description?: string;
  vicDates?: VicDatesSnapshot | null;
};

export const EventDatePicker = ({
  id,
  dates,
  onChange,
  error,
  description = 'At least one date is required to publish',
  vicDates = null,
}: EventDatePickerProps) => {
  const selectedIsoDates = normalizeFormDates(dates);
  const selectedDates = isoDatesToLocalDates(selectedIsoDates);
  const [displayMonth, setDisplayMonth] = React.useState(
    () => selectedDates[0] ?? new Date(),
  );

  React.useEffect(() => {
    setDisplayMonth(selectedDates[0] ?? new Date());
  }, [id]);

  const handleSelect = (next: Date[] | undefined) => {
    onChange(formDatesFromSelection(next));
  };

  const handleClearTag = (iso: string) => {
    const next = removeIsoDate(selectedIsoDates, iso);
    onChange(next.length > 0 ? next : ['']);
  };

  const holidayWarnings = vicDates
    ? selectedIsoDates
        .map((iso) => {
          const warning = dateHolidayWarning(iso, vicDates);
          return warning
            ? formatHolidayWarning(iso, warning, formatEventDisplayDate)
            : null;
        })
        .filter((message): message is string => message !== null)
    : [];

  return (
    <Stack space="small">
      <Text weight="strong" id={`${id}-label`}>
        Dates
      </Text>
      {error ? <Text tone="critical">{error}</Text> : null}
      <Text tone="secondary">{description}</Text>
      <Box className={styles.calendarWrapper}>
        <DayPicker
          mode="multiple"
          weekStartsOn={1}
          month={displayMonth}
          onMonthChange={setDisplayMonth}
          selected={selectedDates}
          onSelect={handleSelect}
          aria-labelledby={`${id}-label`}
        />
      </Box>
      {selectedIsoDates.length > 0 ? (
        <Inline space="small">
          {selectedIsoDates.map((iso) => (
            <Tag
              key={iso}
              size="small"
              onClear={() => handleClearTag(iso)}
              clearLabel={`Remove ${formatEventDisplayDate(iso)}`}
            >
              {formatEventDisplayDate(iso)}
            </Tag>
          ))}
        </Inline>
      ) : null}
      {holidayWarnings.map((message) => (
        <Text key={message} tone="caution">
          {message}
        </Text>
      ))}
    </Stack>
  );
};
